function handleKeywordClick(keywordId, store) {
  store.commit('keywords/setSelectedKeywordId', keywordId)
}

const foundKeywordIds = new Set()

/**
 * Find all keyword occurrences in a text string, avoiding overlaps
 * @param {string} text - The text to search in
 * @param {Array} keywords - Array of keyword objects
 * @returns {Array} Array of keyword occurrence objects
 */
function findKeywordOccurrences(text, keywords) {
  const lowerCaseText = text.toLowerCase()
  const occurrences = []

  keywords.forEach((keyword) => {
    const keywordLower = keyword.name.toLowerCase()
    let searchIndex = 0

    // Find all occurrences of this keyword in the text
    let keywordIndex = lowerCaseText.indexOf(keywordLower, searchIndex)
    while (keywordIndex > -1) {
      const endIndex = keywordIndex + keyword.name.length

      // Check if this occurrence overlaps with any already found keywords
      // eslint-disable-next-line no-loop-func
      const overlaps = occurrences.some((occurrence) => {
        return keywordIndex < occurrence.endIndex && endIndex > occurrence.startIndex
      })

      if (!overlaps) {
        occurrences.push({
          keyword,
          startIndex: keywordIndex,
          endIndex,
        })
        foundKeywordIds.add(keyword.id)
      }

      searchIndex = keywordIndex + 1
      keywordIndex = lowerCaseText.indexOf(keywordLower, searchIndex)
    }
  })

  return occurrences
}

/**
 * Create a keyword span element with click handler
 * @param {Object} keyword - The keyword object
 * @param {string} text - The text content for the span
 * @param {Object} store - The store object
 * @returns {HTMLElement} The created span element
 */
function createKeywordSpan(keyword, text, store) {
  const keywordNode = document.createElement('span')
  keywordNode.classList.add('c-keyword')
  keywordNode.addEventListener('click', (event) => {
    event.stopPropagation()
    handleKeywordClick(keyword.id, store)
  })
  keywordNode.textContent = text
  return keywordNode
}

/**
 * Create a document fragment containing the text written in front of the keyword, the highlighted keyword span and the text written after the keyword
 * E.g. in pseudocode for the keyword "bread":
 * "I like bread, it's tasty"
 * becomes:
 * [
 *   'I like ' (text node)
 *   '<span class="c-keyword">bread</span>' (html span element)
 *   ', it's tasty' (text node)
 * ]
 * @param {string} originalContent - The original text content
 * @param {Array} keywordOccurrences - Array of keyword occurrences
 * @param {Object} store - The store object
 * @returns {DocumentFragment} The fragment with highlighted content
 */
function createHighlightedFragment(originalContent, keywordOccurrences, store) {
  // Sort by start index in descending order to process from end to beginning
  // This prevents index shifting issues when replacing text
  keywordOccurrences.sort((a, b) => b.startIndex - a.startIndex)

  // A document fragment is basically just a list of nodes
  const fragment = new DocumentFragment()
  let lastIndex = originalContent.length

  // Process keywords from end to beginning
  keywordOccurrences.forEach(({ keyword, startIndex, endIndex }) => {
    // Add text after this keyword (if any)
    if (lastIndex > endIndex) {
      fragment.insertBefore(
        document.createTextNode(originalContent.substring(endIndex, lastIndex)),
        fragment.firstChild,
      )
    }

    // Add the highlighted keyword
    const keywordSpan = createKeywordSpan(
      keyword,
      originalContent.substring(startIndex, endIndex),
      store,
    )
    fragment.insertBefore(keywordSpan, fragment.firstChild)

    lastIndex = startIndex
  })

  // Add any remaining text at the beginning
  if (lastIndex > 0) {
    fragment.insertBefore(
      document.createTextNode(originalContent.substring(0, lastIndex)),
      fragment.firstChild,
    )
  }

  return fragment
}

/**
 * Takes an html node and makes sure all keywords are highlighted with a span and
 * have a click event handler
 */
function insertKeywordNodes(node, store) {
  if (!node) {
    return node
  }
  // We don't want to replace any keywords in an existing keyword
  if (node.tagName === 'SPAN' && node.classList.contains('c-keyword')) {
    return node
  }
  // We don't want to replace any keywords in links
  if (node.tagName === 'A') {
    return node
  }
  // Only text nodes have actual text, so we only need to look for keywords in those.
  if (node.nodeType === node.TEXT_NODE) {
    const originalContent = node.textContent
    const keywords = store.getters['keywords/allKeywords']

    // Find all keyword occurrences in this text node
    const keywordOccurrences = findKeywordOccurrences(originalContent, keywords)

    // If we found keywords, replace the text node with highlighted content
    if (keywordOccurrences.length > 0) {
      node = createHighlightedFragment(originalContent, keywordOccurrences, store)
    }
  }
  if (node.childNodes) {
    node.childNodes.forEach((childNode) => {
      // Replace each child with the node that is returned by the insertKeywordNodes function
      // If no keyword is found, this will effectively do nothing, otherwise it will insert the
      // nodes that are created by the `highlightKeyword` function.
      node.replaceChild(insertKeywordNodes(childNode, store), childNode)
    })
  }
  return node
}

/**
 * We don't initially load all keyword descriptions to reduce traffic if there are a lot of keywords
 * When we detect a keyword being used, we preload those descriptions, so that they are available once the admin clicks on them
 */
function preloadMissingKeywordDescriptions(store) {
  const keywordsWithoutDescription = []
  foundKeywordIds.forEach((keywordId) => {
    if (!store.getters['keywords/descriptionExists'](keywordId)) {
      keywordsWithoutDescription.push(keywordId)
    }
  })
  if (keywordsWithoutDescription.length > 0) {
    store.dispatch('keywords/updateDescriptions', keywordsWithoutDescription)
  }
}

/**
 * Takes an html element and makes sure that all keywords in it are replaced with a span that highlights
 * the keyword and has an appropriate click event.
 */
export async function highlightKeywords(el, store) {
  const keywords = store.getters['keywords/allKeywords']
  if (!keywords) {
    await store.dispatch('keywords/updateKeywords')
  }
  foundKeywordIds.clear()
  const outputNode = insertKeywordNodes(el, store)
  preloadMissingKeywordDescriptions(store)
  return outputNode
}
